(function () {
  if (customElements.get('store-finder')) return;

  var MILE_COUNTRIES = ['US', 'GB'];

  // Common ways people name a country in the search box, mapped to ISO2.
  // Only needed for names that are not already the country string in the data.
  var COUNTRY_ALIASES = {
    'uk': 'GB', 'gb': 'GB', 'britain': 'GB', 'great britain': 'GB',
    'england': 'GB', 'scotland': 'GB', 'wales': 'GB',
    'usa': 'US', 'us': 'US', 'america': 'US',
    'aus': 'AU', 'oz': 'AU',
    'deutschland': 'DE',
    'schweiz': 'CH', 'suisse': 'CH', 'svizzera': 'CH',
    'czechia': 'CZ',
    'holland': 'NL'
  };

  function norm(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function haversine(a, b) {
    var R = 6371;
    var dLat = (b.lat - a.lat) * Math.PI / 180;
    var dLng = (b.lng - a.lng) * Math.PI / 180;
    var la1 = a.lat * Math.PI / 180;
    var la2 = b.lat * Math.PI / 180;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(la1) * Math.cos(la2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function escapeHtml(value) {
    return (value || '').toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function mapsUrl(store) {
    var parts = [store.n, store.a, store.p, store.c, store.co].filter(Boolean);
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(parts.join(', '));
  }

  class StoreFinder extends HTMLElement {
    connectedCallback() {
      var raw = this.querySelector('[data-stockists]');
      try {
        this.stores = JSON.parse(raw.textContent);
      } catch (e) {
        this.stores = [];
        return;
      }

      this.stores.forEach(function (store, index) {
        store._i = index;
        store._search = norm([store.n, store.c, store.p, store.co].join(' '));
        store._name = norm(store.n);
        store._place = norm([store.c, store.p, store.co].join(' '));
      });

      this.physical = this.stores.filter(function (s) { return !s.online && typeof s.lat === 'number'; });
      this.onlineStores = this.stores.filter(function (s) { return s.online; });

      this.buildCountryTokens();

      this.pageSize = parseInt(this.dataset.pageSize, 10) || 18;
      this.shown = this.pageSize;
      this.origin = null;
      this.originLabel = '';

      this.queryInput = this.querySelector('[data-query]');
      this.countrySelect = this.querySelector('[data-country]');
      this.results = this.querySelector('[data-results]');
      this.statusEl = this.querySelector('[data-status]');
      this.emptyEl = this.querySelector('[data-empty]');
      this.moreBtn = this.querySelector('[data-more]');
      this.geoBtn = this.querySelector('[data-geo]');
      this.geoLabel = this.querySelector('[data-geo-label]');
      this.geoResetLabel = this.geoLabel.textContent.trim();
      this.clearBtn = this.querySelector('[data-clear]');
      this.onlineWrap = this.querySelector('[data-online]');
      this.onlineList = this.querySelector('[data-online-list]');

      this.buildCountries();
      this.bind();
      this.renderOnline();
      this.render();
    }

    // Every string that can name a country in the search box, longest first so
    // "united kingdom" is tested before "united".
    buildCountryTokens() {
      var seen = {};
      var tokens = [];

      this.stores.forEach(function (s) {
        var t = norm(s.co);
        if (t && !seen[t]) { seen[t] = 1; tokens.push({ token: t, cc: s.cc }); }
        var code = norm(s.cc);
        if (code && !seen[code]) { seen[code] = 1; tokens.push({ token: code, cc: s.cc }); }
      });

      Object.keys(COUNTRY_ALIASES).forEach(function (alias) {
        var t = norm(alias);
        if (t && !seen[t]) { seen[t] = 1; tokens.push({ token: t, cc: COUNTRY_ALIASES[alias] }); }
      });

      tokens.sort(function (a, b) { return b.token.length - a.token.length; });
      this.countryTokens = tokens;
    }

    // Split "melbourne australia" into place "melbourne" + country AU.
    // Returns null when the query names no country.
    splitCountry(query) {
      for (var i = 0; i < this.countryTokens.length; i++) {
        var t = this.countryTokens[i];
        if (query === t.token) return { place: '', cc: t.cc, whole: true };
        if (query.slice(-(t.token.length + 1)) === ' ' + t.token) {
          return { place: query.slice(0, query.length - t.token.length - 1).trim(), cc: t.cc, whole: false };
        }
        if (query.slice(0, t.token.length + 1) === t.token + ' ') {
          return { place: query.slice(t.token.length + 1).trim(), cc: t.cc, whole: false };
        }
      }
      return null;
    }

    // Among several stores answering to the same place name, pick the one in the
    // country with the most doors there. "Melbourne" has 3 in Victoria and 1 in
    // Derbyshire, so Victoria wins. The visitor's own market breaks a tie.
    pickPlace(pool) {
      var byCc = {};
      pool.forEach(function (s) { byCc[s.cc] = (byCc[s.cc] || 0) + 1; });

      var pref = (this.dataset.defaultCountry || '').toUpperCase();
      var best = pool[0];
      var bestScore = -1;

      for (var i = 0; i < pool.length; i++) {
        var s = pool[i];
        var score = (byCc[s.cc] * 2) + (s.cc === pref ? 1 : 0);
        if (score > bestScore) { bestScore = score; best = s; }
      }
      return best;
    }

    matchPlace(place, cc) {
      var exact = [];
      var partial = [];

      for (var i = 0; i < this.physical.length; i++) {
        var store = this.physical[i];
        if (cc && store.cc !== cc) continue;
        if (norm(store.c) === place || norm(store.p) === place) exact.push(store);
        else if (place && store._place.indexOf(place) !== -1) partial.push(store);
      }

      return exact.length ? exact : partial;
    }

    resolveOrigin(query) {
      if (!query) return null;

      var split = this.splitCountry(query);
      var pool = [];
      var countryOnly = false;

      if (split && split.whole) {
        // Just a country name: centre on that country's doors.
        pool = this.matchPlace('', split.cc);
        if (!pool.length) pool = this.physical.filter(function (s) { return s.cc === split.cc; });
        countryOnly = true;
      } else if (split && split.place) {
        pool = this.matchPlace(split.place, split.cc);
        // Named a country that has no such town: fall back to the town anywhere
        // rather than dead-ending on "No stockists found".
        if (!pool.length) pool = this.matchPlace(split.place, '');
      }

      if (!pool.length) pool = this.matchPlace(query, '');
      if (!pool.length) return null;

      var hit = this.pickPlace(pool);

      var label;
      if (countryOnly) {
        label = hit.co;
      } else {
        var place = (split && split.place) ? split.place : query;
        label = (norm(hit.c) === place || (hit._place.indexOf(place) !== -1 && place.length > 2))
          ? (hit.c || hit.co)
          : hit.co;
      }

      return { lat: hit.lat, lng: hit.lng, label: label, cc: hit.cc };
    }

    buildCountries() {
      var seen = {};
      this.physical.concat(this.onlineStores).forEach(function (s) {
        if (!seen[s.cc]) seen[s.cc] = { code: s.cc, name: s.co, count: 0 };
        seen[s.cc].count += 1;
      });

      var list = Object.keys(seen).map(function (k) { return seen[k]; });
      list.sort(function (a, b) { return a.name.localeCompare(b.name); });

      var html = '<option value="">All countries</option>';
      list.forEach(function (c) {
        html += '<option value="' + c.code + '">' + escapeHtml(c.name) + ' (' + c.count + ')</option>';
      });
      this.countrySelect.innerHTML = html;

      var preset = (this.dataset.defaultCountry || '').toUpperCase();
      if (preset && seen[preset]) this.countrySelect.value = preset;
    }

    bind() {
      var self = this;
      var timer;

      this.queryInput.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          self.shown = self.pageSize;
          self.origin = null;
          self.originLabel = '';
          self.setGeoState('');
          self.clearBtn.hidden = self.queryInput.value.length === 0;
          self.render();
        }, 140);
      });

      this.clearBtn.addEventListener('click', function () {
        self.queryInput.value = '';
        self.clearBtn.hidden = true;
        self.shown = self.pageSize;
        self.origin = null;
        self.originLabel = '';
        self.setGeoState('');
        self.render();
        self.queryInput.focus();
      });

      this.countrySelect.addEventListener('change', function () {
        self.shown = self.pageSize;
        self.render();
      });

      this.moreBtn.addEventListener('click', function () {
        self.shown += self.pageSize;
        self.render();
      });

      this.geoBtn.addEventListener('click', function () { self.locate(); });
    }

    setGeoState(state) {
      if (state) this.geoBtn.dataset.state = state;
      else this.geoBtn.removeAttribute('data-state');
    }

    locate() {
      var self = this;
      if (!navigator.geolocation) {
        this.geoLabel.textContent = 'Not available';
        return;
      }
      this.setGeoState('busy');
      navigator.geolocation.getCurrentPosition(
        function (position) {
          self.origin = { lat: position.coords.latitude, lng: position.coords.longitude };
          self.originLabel = 'your location';
          self.queryInput.value = '';
          self.clearBtn.hidden = true;
          self.countrySelect.value = '';
          self.shown = self.pageSize;
          self.setGeoState('active');
          self.render();
          self.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        function () {
          self.setGeoState('');
          self.geoLabel.textContent = 'Location blocked';
          setTimeout(function () { self.geoLabel.textContent = self.geoResetLabel; }, 2600);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    }

    compute() {
      var query = norm(this.queryInput.value);
      var country = this.countrySelect.value;
      var all = this.physical;
      var mode = 'browse';
      var origin = this.origin;
      var label = this.originLabel;

      var pool = country
        ? all.filter(function (s) { return s.cc === country; })
        : all.slice();

      if (query && !origin) {
        // A retailer name beats a place name: "Bogong" is a shop, not a town.
        var named = all.filter(function (s) { return s._name.indexOf(query) !== -1; });
        var placeOrigin = named.length ? null : this.resolveOrigin(query);

        if (placeOrigin) {
          // A place in another country outranks the country filter: the visitor named the place.
          if (country && placeOrigin.cc !== country) {
            this.countrySelect.value = '';
            country = '';
            pool = all.slice();
          }
          origin = placeOrigin;
          label = placeOrigin.label;
          mode = 'near';
        } else {
          var matches = pool.filter(function (s) { return s._name.indexOf(query) !== -1; });
          if (!matches.length) {
            matches = pool.filter(function (s) { return s._search.indexOf(query) !== -1; });
          }

          // Nothing in the selected country but the retailer exists elsewhere: widen, do not dead-end.
          if (!matches.length && country) {
            var worldwide = all.filter(function (s) { return s._search.indexOf(query) !== -1; });
            if (worldwide.length) {
              this.countrySelect.value = '';
              country = '';
              matches = worldwide;
            }
          }

          pool = matches;
          mode = 'name';
        }
      } else if (origin) {
        mode = 'near';
      }

      if (origin) {
        pool.forEach(function (s) { s._d = haversine(origin, s); });
        pool.sort(function (a, b) { return a._d - b._d; });
      } else {
        pool.forEach(function (s) { delete s._d; });
        pool.sort(function (a, b) {
          return (a.co + a.c + a.n).localeCompare(b.co + b.c + b.n);
        });
      }

      return { pool: pool, mode: mode, label: label, query: query, origin: origin };
    }

    formatDistance(km, cc) {
      var useMiles = MILE_COUNTRIES.indexOf((cc || this.countrySelect.value || '').toUpperCase()) !== -1;
      var value = useMiles ? km * 0.621371 : km;
      var unit = useMiles ? 'mi' : 'km';
      if (value < 10) return value.toFixed(1) + ' ' + unit;
      return Math.round(value) + ' ' + unit;
    }

    render() {
      var state = this.compute();
      var pool = state.pool;
      var visible = pool.slice(0, this.shown);
      var self = this;

      var html = '';
      visible.forEach(function (store) {
        var addressLines = [];
        if (store.a) addressLines.push(escapeHtml(store.a));
        var line2 = [store.p, store.c].filter(Boolean).join(' ');
        if (line2) addressLines.push(escapeHtml(line2));
        addressLines.push(escapeHtml(store.co));

        var region = escapeHtml(store.c || store.co);
        var distance = typeof store._d === 'number'
          ? '<span class="store-finder__distance">' + self.formatDistance(store._d, store.cc) + '</span>'
          : '';

        var links = '<a href="' + mapsUrl(store) + '" target="_blank" rel="noopener">Directions</a>';
        if (store.w) {
          links += '<a href="' + escapeHtml(store.w) + '" target="_blank" rel="noopener">Website</a>';
        }

        html += '<li class="store-finder__card">' +
          '<div class="store-finder__meta"><span class="store-finder__region">' + region + '</span>' + distance + '</div>' +
          '<h3 class="store-finder__name">' + escapeHtml(store.n) + '</h3>' +
          '<p class="store-finder__address">' + addressLines.join('<br>') + '</p>' +
          '<div class="store-finder__links">' + links + '</div>' +
          '</li>';
      });

      this.results.innerHTML = html;
      this.results.hidden = pool.length === 0;
      this.emptyEl.hidden = pool.length !== 0;
      this.moreBtn.hidden = pool.length <= this.shown;
      this.moreBtn.textContent = 'Show ' + Math.min(this.pageSize, pool.length - this.shown) + ' more';

      this.statusEl.innerHTML = this.statusText(state, pool.length);
    }

    statusText(state, count) {
      var noun = count === 1 ? 'stockist' : 'stockists';
      if (count === 0) return 'No stockists found';

      if (state.mode === 'near' && state.label) {
        return count + ' ' + noun + ' &middot; nearest to <em>' + escapeHtml(state.label) + '</em>';
      }
      if (state.mode === 'name' && state.query) {
        return count + ' ' + noun + ' matching <em>' + escapeHtml(this.queryInput.value.trim()) + '</em>';
      }
      var country = this.countrySelect.options[this.countrySelect.selectedIndex];
      if (this.countrySelect.value && country) {
        return count + ' ' + noun + ' in <em>' + escapeHtml(country.text.replace(/\s*\(\d+\)$/, '')) + '</em>';
      }
      return count + ' ' + noun + ' worldwide';
    }

    renderOnline() {
      if (!this.onlineStores.length) return;
      var html = '';
      this.onlineStores.forEach(function (store) {
        var text = escapeHtml(store.n) + ' <span style="opacity:.5">' + escapeHtml(store.co) + '</span>';
        html += '<li>' + (store.w
          ? '<a href="' + escapeHtml(store.w) + '" target="_blank" rel="noopener">' + text + '</a>'
          : text) + '</li>';
      });
      this.onlineList.innerHTML = html;
      this.onlineWrap.hidden = false;
    }
  }

  customElements.define('store-finder', StoreFinder);
})();